import { css } from 'styled-components';

/**
 * Chat prose scale.
 *
 * One step down from the Claude Code desktop/web rhythm this started at: at
 * 15px/1.65 the transcript read a size larger than the chrome around it
 * (11–13px status rows, pickers, banners) and users reported the chat text as
 * "too big". 14px keeps the sans body comfortable for multi-paragraph agent
 * answers while sitting one clear step above the chrome rather than two.
 *
 * These five constants ARE the knob: everything downstream — block margins,
 * list indents, heading sizes — is expressed in `em`, so changing the numbers
 * here rescales the whole transcript proportionally. Nothing else needs to
 * move except the two derived px constants that cite them by name
 * (`TEXT_HALF_LEADING_PX` and `SINGLE_LINE_TEXTAREA_HEIGHT`, both in the
 * components that own them).
 *
 * Shared between the transcript (`MessageBubble`) and the chrome that frames
 * it (`ChatView`'s composer and status rows) so the box you type into never
 * drifts off-scale from the messages it produces.
 */

/** Prose size for message text and the composer. */
export const CHAT_FONT_SIZE = '0.875rem'; /* 14px */

/** Prose line-height — loose enough for multi-paragraph agent answers. */
export const CHAT_LINE_HEIGHT = '1.6';

/**
 * Monospace size for fenced code, markdown tables, and command previews.
 * Deliberately a step below the prose size: mono glyphs read larger at the
 * same nominal size, so matching 14px would make code shout over the text.
 */
export const CHAT_MONO_FONT_SIZE = '0.75rem'; /* 12px */

/** Monospace line-height — tighter than prose, code lines are short. */
export const CHAT_MONO_LINE_HEIGHT = '1.55';

/**
 * Vertical gap between consecutive turns in the transcript.
 *
 * Tracks the prose size: this is the only thing separating one turn from the
 * next (no bubble borders on assistant turns), so it has to stay visibly
 * larger than the ~22px line box inside a turn — but at the smaller type, 24px
 * left the transcript looking gappy rather than grouped.
 */
export const CHAT_TURN_GAP = '20px';

/** Body scale for anything rendering chat prose. */
export const CHAT_PROSE = css`
  font-size: ${CHAT_FONT_SIZE};
  line-height: ${CHAT_LINE_HEIGHT};
`;

/** Type scale for fenced code / mono boxes inside the transcript. */
export const CHAT_MONO_TYPE = css`
  font-size: ${CHAT_MONO_FONT_SIZE};
  line-height: ${CHAT_MONO_LINE_HEIGHT};
`;
